import { Fragment, useCallback, useEffect, useState } from "react";
import { FaLaptopCode } from "react-icons/fa6";
import { GoCodeSquare } from "react-icons/go";
import { GrFormClose } from "react-icons/gr";
import { PiBrainDuotone } from "react-icons/pi";
import { RiBrainFill } from "react-icons/ri";
import Logo from "../../assets/images/logo.png";
import { useTalentCbtDetailsQuery, useUserOverallRatingQuery } from "../../reactWrapper/endpoints/talent/talent.api";
import Rating from "../Rating/Rating";
import { Progress } from "../ui/progress";
import { Loader } from "../Loader/PageLoader";

const DeepProfile = ({ userId }) => {
  const { talentCbtDetails } = useTalentCbtDetailsQuery(userId);
  const [open, setOpen] = useState(false);
  const { overallRating } = useUserOverallRatingQuery(userId);
  const [frameworks, setFrameworks] = useState([]);
  const [skills, setSkills] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [result, setResult] = useState();

  const filterDetails = useCallback(() => {
    if (!result) return;

    let idx = 1;

    while (
      `language${idx}_name` in result ||
      `skill${idx}_name` in result ||
      `framework${idx}_name` in result
    ) {
      if (`language${idx}_name` in result) {
        const nameField = `language${idx}_name`;
        const scoreField = `language${idx}_percent_score`;
        if (result[nameField] !== null) {
          const data = {
            name: result[nameField],
            percentage: result[scoreField],
          };
          setLanguages((prev) => prev.concat([data]));
        }
      }

      if (`framework${idx}_name` in result) {
        const nameField = `framework${idx}_name`;
        const scoreField = `framework${idx}_percent_score`;
        if (result[nameField] !== null) {
          const data = {
            name: result[nameField],
            percentage: result[scoreField],
          };
          setFrameworks((prev) => prev.concat([data]));
        }
      }

      if (`skill${idx}_name` in result) {
        const nameField = `skill${idx}_name`;
        const scoreField = `skill${idx}_percent_score`;
        if (result[nameField] !== null) {
          const data = {
            name: result[nameField],
            percentage: result[scoreField],
          };
          setSkills((prev) => prev.concat([data]));
        }
      }
      idx++;
    }
  }, [talentCbtDetails]);

  useEffect(() => {
    setLanguages([]);
    setFrameworks([]);
    setSkills([]);
    filterDetails();
    setResult(talentCbtDetails?.userCbtResult[0]);
  }, [talentCbtDetails]);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs font-semibold text-brand-orange hover:underline"
      >
        View Deep Profile
      </button>
      <Fragment>
        <div
          className={`z-30 ${open ? "flex" : "flex -translate-y-full"
            } transition-ease fixed inset-x-0 -top-10 bottom-0 w-full items-center justify-center overflow-auto bg-brand-navy/50`}
        >
          <div
            onClick={() => setOpen(false)}
            className="transparent absolute right-0 top-0 h-full w-full"
          />
          <section
            className={`relative z-20 flex flex-col space-y-3 overflow-hidden rounded-2xl bg-card px-2 py-5`}
          >
            <div
              onClick={() => setOpen(false)}
              className="absolute right-5 top-5 block hover:cursor-pointer"
            >
              <GrFormClose className="h-6 w-6" />
            </div>

            <div className="h-[85vh] min-h-[60vh] max-w-[80%] w-full md:w-[70vw] hidden-scrollbar overflow-hidden overflow-y-auto rounded-lg px-1 md:top-0 mx-auto">
              {talentCbtDetails ? (
                <div className="flex flex-col items-center w-full overflow-x-hidden md:px-10 space-y-5">
                  <div className={`w-16 h-16 md:w-36 md:h-36 rounded-full overflow-hidden`}>
                    <img
                      src={talentCbtDetails?.photo ?? ""}
                      className='w-full h-full'
                      alt={talentCbtDetails?.firstName}
                    />
                  </div>

                  <div className="w-full flex flex-col justify-center items-center">
                    <p className="text-xl font-semibold capitalize text-brand-navy">
                      {talentCbtDetails?.firstName} {talentCbtDetails?.lastName?.slice(0, 1)}.
                    </p>
                    <p className="text-sm font-medium capitalize text-muted-foreground">
                      {talentCbtDetails?.display_title || talentCbtDetails?.primaryStack}{" "}
                      {talentCbtDetails?.role},{" "}
                      {talentCbtDetails?.continent}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">{talentCbtDetails?.short_bio}</p>
                  </div>

                  <div className="flex flex-col space-y-3 mb-10 w-full">
                    <div className="flex space-x-3 items-center">
                      <img
                        src={Logo}
                        alt="droomwork-logo"
                        className="w-10 h-10"
                      />
                      <p className="md:text-lg font-bold text-brand-navy">
                        Experience on Droomwork
                      </p>
                    </div>
                    <div className="grid grid-cols-2 md:px-5 gap-3 pb-5 md:place-items-start text-sm md:text-base w-full">
                      <p>Work Quality</p>
                      <Rating rating={overallRating?.code_quality ?? 0} />
                      <p>Productivity</p>
                      <Rating rating={overallRating?.productivity ?? 0} />
                      <p className="whitespace-nowrap">
                        Reliability
                      </p>
                      <Rating rating={overallRating?.reliability ?? 0} />
                      <p>Communication</p>
                      <Rating rating={overallRating?.communications ?? 0} />
                      <p>Timelines</p>
                      <Rating rating={overallRating?.timelines ?? 0} />
                    </div>
                  </div>

                  <div className="w-full">
                    {/* Language */}
                    <section className="flex flex-col space-y-3 mb-10">
                      <div className="flex space-x-3 items-center">
                        <GoCodeSquare className="w-7 h-7" />
                        <p className="text-lg font-bold text-brand-navy">Language</p>
                      </div>
                      <div className="flex flex-col space-y-3">
                        <div className="grid grid-cols-2">
                          <p className="col-span-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Language</p>
                          <p className="col-span-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Vetting Result
                          </p>
                        </div>
                        {languages.map((language, idx) => (
                          <div key={idx} className="grid grid-cols-2">
                            <p className="col-span-1 font-medium text-brand-navy">
                              {language.name}
                            </p>
                            <div className="col-span-1 font-medium flex space-x-3 items-center">
                              <Progress
                                value={language.percentage}
                                className="h-2 w-[40%] bg-surface"
                              />
                              <p className="tabular-nums text-brand-navy">{language.percentage}%</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                    {/* Framework */}
                    <section className="flex flex-col space-y-3 mb-10">
                      <div className="flex space-x-3 items-center">
                        <FaLaptopCode className="w-7 h-7" />
                        <p className="text-lg font-bold text-brand-navy">Framework</p>
                      </div>
                      <div className="flex flex-col space-y-3">
                        <div className="grid grid-cols-2">
                          <p className="col-span-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Framework</p>
                          <p className="col-span-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Vetting Result
                          </p>
                        </div>
                        {frameworks.map((framework, idx) => (
                          <div key={idx} className="grid grid-cols-2">
                            <p className="col-span-1 font-medium text-brand-navy">
                              {framework.name}
                            </p>
                            <div className="col-span-1 font-medium flex space-x-3 items-center">
                              <Progress
                                value={framework.percentage}
                                className="h-2 w-[40%] bg-surface"
                              />
                              <p className="tabular-nums text-brand-navy">{framework.percentage}%</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                    {/* Skill */}
                    <section className="flex flex-col space-y-3 mb-10">
                      <div className="flex space-x-3 items-center">
                        <PiBrainDuotone className="w-7 h-7" />
                        <p className="text-lg font-bold text-brand-navy">Skill</p>
                      </div>
                      <div className="flex flex-col space-y-3">
                        <div className="grid grid-cols-2">
                          <p className="col-span-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Skill</p>
                          <p className="col-span-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Vetting Result
                          </p>
                        </div>
                        {skills.map((skill, idx) => (
                          <div key={idx} className="grid grid-cols-2">
                            <p className="col-span-1 font-medium text-brand-navy">
                              {skill.name}
                            </p>
                            <div className="col-span-1 font-medium flex space-x-3 items-center">
                              <Progress
                                value={skill.percentage}
                                className="h-2 w-[40%] bg-surface"
                              />
                              <p className="tabular-nums text-brand-navy">{skill.percentage}%</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                    {/* Others => security, version control, internet */}
                    <section className="flex flex-col space-y-3 mb-10">
                      <div className="flex space-x-3 items-center">
                        <RiBrainFill className="w-7 h-7" />
                        <p className="text-lg font-bold text-brand-navy">Others</p>
                      </div>
                      <div className="flex flex-col space-y-3">
                        <div className="grid grid-cols-2">
                          <p className="col-span-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Section</p>
                          <p className="col-span-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Vetting Result
                          </p>
                        </div>
                        <div className="grid grid-cols-2">
                          <p className="col-span-1 font-medium text-brand-navy">Internet</p>
                          <div className="col-span-1 font-medium flex space-x-3 items-center">
                            <Progress
                              value={result?.internet_score || 0}
                              className="h-2 w-[40%] bg-surface"
                            />
                            <p className="tabular-nums text-brand-navy">{result?.internet_score}%</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2">
                          <p className="col-span-1 font-medium text-brand-navy">
                            Version Control
                          </p>
                          <div className="col-span-1 font-medium flex space-x-3 items-center">
                            <Progress
                              value={result?.version_control_score || 0}
                              className="h-2 w-[40%] bg-surface"
                            />
                            <p className="tabular-nums text-brand-navy">{result?.version_control_score}%</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2">
                          <p className="col-span-1 font-medium text-brand-navy">Security</p>
                          <div className="col-span-1 font-medium flex space-x-3 items-center">
                            <Progress
                              value={result?.security_score || 0}
                              className="h-2 w-[40%] bg-surface"
                            />
                            <p className="tabular-nums text-brand-navy">{result?.security_score}%</p>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              ) : (
                <div className="h-full w-full flex justify-center items-center">
                  <Loader />
                </div>
              )}
            </div>
          </section>
        </div>
      </Fragment>
    </div>
  );
};

export default DeepProfile;
